import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import LiveScoring from "./LiveScoring";
import EntityLogo from "@/components/EntityLogo";
import {
  Play, Radio, Video, MessageSquare, Info, ClipboardList, BarChart3, ListOrdered,
  Film, Trophy, ChevronLeft, CloudSun, Radar, Users, Target,
} from "lucide-react";

const DIAMOND = [
  { pos: "P", x: 50, y: 62 }, { pos: "C", x: 50, y: 90 }, { pos: "1B", x: 74, y: 55 },
  { pos: "2B", x: 62, y: 40 }, { pos: "SS", x: 38, y: 40 }, { pos: "3B", x: 26, y: 55 },
  { pos: "LF", x: 20, y: 22 }, { pos: "CF", x: 50, y: 12 }, { pos: "RF", x: 80, y: 22 },
];
const TABS = ["Recap", "Live", "Box Score", "Play-by-Play", "Statistics", "Video", "Highlights", "Chat", "Info"];

export default function LiveGame() {
  const { id } = useParams();
  const { user } = useAuth();
  const [g, setG] = useState(null);
  const [team, setTeam] = useState(null);
  const [roster, setRoster] = useState([]);

  const load = () => api.get(`/live/games/${id}`).then(({ data }) => {
    setG(data);
    api.get(`/live/teams/${data.team_id}`).then((r) => { setTeam(r.data); setRoster(r.data.roster || []); }).catch(() => {});
  }).catch(() => setG(false));
  useEffect(() => { load(); }, [id]);

  if (g === false) return <Shell><Empty>Game not found.</Empty></Shell>;
  if (!g) return <Shell><Empty>Loading game…</Empty></Shell>;

  const canManage = user && (user.role === "admin" || user.role === "super_admin" || user.id === g.created_by);
  const isLive = g.status === "live" || g.status === "final";

  return (
    <Shell>
      {team && <div className="max-w-5xl mx-auto px-6 pt-4"><Link to={`/live/teams/${team.slug}`} className="text-xs text-slate-500 flex items-center gap-1 hover:text-[#E63946]"><ChevronLeft className="w-4 h-4" /> {team.name}</Link></div>}
      {isLive
        ? <LiveShell g={g} team={team} roster={roster} canManage={canManage} onFinal={load} />
        : <Pregame g={g} team={team} roster={roster} canManage={canManage} onChange={load} />}
    </Shell>
  );
}

/* ---------------------------------------------------------------- Pregame */
function Pregame({ g, team, roster, canManage, onChange }) {
  const [defense, setDefense] = useState({});
  const [pitcher, setPitcher] = useState("");
  const homeT = g.home_team || {};
  const awayT = g.away_team || {};
  const inLineup = Object.values(defense).filter(Boolean);
  const bench = roster.filter((p) => !inLineup.includes(p.player_id) && p.player_id !== pitcher);

  const saveLineup = (lock) => {
    const batting_order = Object.entries(defense).filter(([, pid]) => pid).map(([pos, pid], i) => ({ player_id: pid, pos, order: i + 1 }));
    api.post(`/live/games/${g.id}/lineup`, { batting_order, pitchers: pitcher ? [pitcher] : [], bench: bench.map((b) => b.player_id), lock })
      .then(() => { toast.success(lock ? "Lineup locked" : "Lineup saved"); onChange(); }).catch(() => toast.error("Save failed"));
  };
  const startGame = () => api.post(`/live/games/${g.id}/transition`, { to: "live" })
    .then(() => { toast.success("Game is LIVE"); onChange(); }).catch((e) => toast.error(e?.response?.data?.detail || "Failed"));
  const setMode = (mode) => api.post(`/live/games/${g.id}/scoring-mode`, { mode })
    .then(() => { toast.success(`${mode === "advanced" ? "Advanced" : "Basic"} scoring enabled`); onChange(); }).catch(() => toast.error("Failed"));
  const mode = g.scoring_mode || "basic";

  return (
    <div className="max-w-5xl mx-auto px-6 py-6" data-testid="pregame-view">
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <div className="label-overline text-[#E63946] capitalize">{g.status.replace("_", " ")} · {g.game_type}</div>
        <h1 className="mt-1" data-testid="pregame-matchup">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-2">
              <EntityLogo src={awayT.effective_logo_url || awayT.logo_url} alt={awayT.name} size="w-9 h-9" textSize="text-[9px]" />
              <span data-testid="pregame-away-name" className="font-display text-2xl sm:text-3xl uppercase tracking-tight">{awayT.name || "Away"}</span>
            </span>
            <span className="text-slate-400 text-xl font-semibold">@</span>
            <span className="flex items-center gap-2">
              <EntityLogo src={homeT.effective_logo_url || homeT.logo_url} alt={homeT.name} size="w-9 h-9" textSize="text-[9px]" />
              <span data-testid="pregame-home-name" className="font-display text-2xl sm:text-3xl uppercase tracking-tight">{homeT.name || "Home"}</span>
            </span>
            {g.neutral_site && <span data-testid="pregame-neutral" className="text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded-sm">Neutral Site</span>}
          </div>
        </h1>
        <div className="text-sm text-slate-500 mt-1">{g.date} {g.time || ""} {g.location ? `· ${g.location}` : ""}</div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mt-6">
        <Card icon={Users} title={`Roster & Lineup (${roster.length})`} testid="lineup-setup">
          {roster.length === 0 ? <Empty>Add players to the team roster first.</Empty> : (
            <div className="space-y-2">
              {roster.map((p) => (
                <div key={p.player_id} className="flex items-center gap-2">
                  <span className="text-sm flex-1 truncate">{p.first_name} {p.last_name}</span>
                  <select data-testid={`pos-select-${p.player_id}`} value={Object.keys(defense).find((k) => defense[k] === p.player_id) || ""}
                    onChange={(e) => { const pos = e.target.value; setDefense((d) => { const nd = { ...d }; Object.keys(nd).forEach((k) => { if (nd[k] === p.player_id) delete nd[k]; }); if (pos) nd[pos] = p.player_id; return nd; }); }}
                    className="border border-slate-200 rounded-sm px-2 py-1 text-xs">
                    <option value="">Bench</option>
                    {DIAMOND.map((d) => <option key={d.pos} value={d.pos} disabled={defense[d.pos] && defense[d.pos] !== p.player_id}>{d.pos}</option>)}
                  </select>
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="space-y-6">
          {canManage && (
            <Card icon={Target} title="Scoring Mode" testid="scoring-mode-card">
              <div className="flex gap-2" data-testid="scoring-mode-toggle">
                {[["basic", "Basic", "Log plate-appearance outcomes"], ["advanced", "Advanced", "Pitch-by-pitch, pitching stats, subs"]].map(([m, lbl, desc]) => (
                  <button key={m} data-testid={`mode-${m}-btn`} onClick={() => setMode(m)}
                    className={`flex-1 text-left px-3 py-2 rounded-sm border transition-colors ${mode === m ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white border-slate-200 text-[#0A192F] hover:border-[#E63946]"}`}>
                    <div className="text-sm font-bold uppercase tracking-wider">{lbl}</div>
                    <div className={`text-[10px] mt-0.5 ${mode === m ? "text-white/60" : "text-slate-400"}`}>{desc}</div>
                  </button>
                ))}
              </div>
            </Card>
          )}
          <Card icon={Play} title="Starting Pitcher" testid="starting-pitcher">
            <select data-testid="pitcher-select" value={pitcher} onChange={(e) => setPitcher(e.target.value)} className="w-full border border-slate-200 rounded-sm px-2 py-2 text-sm">
              <option value="">Select pitcher…</option>
              {roster.map((p) => <option key={p.player_id} value={p.player_id}>{p.first_name} {p.last_name}</option>)}
            </select>
          </Card>
          <Card icon={ClipboardList} title="Bench">
            {bench.length ? <div className="flex flex-wrap gap-2">{bench.map((b) => <span key={b.player_id} className="text-xs bg-slate-100 px-2 py-1 rounded-sm">{b.first_name} {b.last_name}</span>)}</div> : <Empty>No bench players.</Empty>}
          </Card>
          <div className="grid grid-cols-2 gap-3">
            <Placeholder icon={CloudSun} label="Weather" />
            <Placeholder icon={Radio} label="Streaming" />
            <Placeholder icon={Radar} label="Pocket Radar" />
            <Placeholder icon={Info} label="Officials / Notes" />
          </div>
        </div>
      </div>

      {canManage && (
        <div className="mt-6 flex flex-wrap gap-3">
          <button data-testid="save-lineup-btn" onClick={() => saveLineup(false)} className="px-4 py-2 border border-slate-300 rounded-sm text-sm font-semibold uppercase tracking-wider">Save Lineup</button>
          <button data-testid="lock-lineup-btn" onClick={() => saveLineup(true)} className="px-4 py-2 border border-slate-300 rounded-sm text-sm font-semibold uppercase tracking-wider">Lock Lineups</button>
          <button data-testid="start-game-btn" onClick={startGame} className="ml-auto px-8 py-3 bg-[#E63946] text-white rounded-sm font-display uppercase tracking-wider flex items-center gap-2 hover:bg-[#c62f3b]"><Play className="w-5 h-5" /> Start Game</button>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- Live shell */
function LiveShell({ g, team, roster, canManage, onFinal }) {
  const endGame = () => {
    if (!window.confirm("End the game and finalize stats? This flows results into team & player records.")) return;
    api.post(`/live/games/${g.id}/transition`, { to: "final" })
      .then(() => { toast.success("Game Final — stats saved to the ecosystem"); onFinal(); })
      .catch(() => toast.error("Failed"));
  };

  return (
    <div data-testid="live-shell">
      <LiveScoring game={g} team={team} roster={roster} canManage={canManage} onChange={onFinal} />
      {canManage && g.status === "live" && (
        <div className="max-w-5xl mx-auto px-6 pb-24">
          <button data-testid="end-game-btn" onClick={endGame} className="w-full py-3 bg-[#0A192F] text-white rounded-sm font-display uppercase tracking-wider hover:bg-[#1a2a4a] transition-colors">End Game (Final)</button>
        </div>
      )}
    </div>
  );
}

const TAB_ICON = { Recap: Film, "Box Score": ClipboardList, "Play-by-Play": ListOrdered, Statistics: BarChart3, Video: Video, Highlights: Trophy, Chat: MessageSquare, Info: Info };
function TabPlaceholder({ tab }) {
  const Icon = TAB_ICON[tab] || Info;
  return <div className="py-16 text-center text-slate-400" data-testid={`tab-content-${tab.toLowerCase().replace(/[^a-z]/g, "-")}`}><Icon className="w-8 h-8 mx-auto mb-3 opacity-40" /><div className="label-overline">{tab}</div><p className="text-sm mt-2">Coming in a future phase — hooks are wired for stats, video, AI recaps & highlights.</p></div>;
}
function TeamScore({ name, score }) { return <div><div className="text-xs text-white/60 truncate">{name}</div><div className="font-data text-5xl">{score}</div></div>; }
function Bases({ bases }) {
  const on = (b) => (bases?.[b] ? "bg-[#E63946]" : "bg-white/20");
  return <div className="relative w-8 h-8"><span className={`absolute left-1/2 top-0 -translate-x-1/2 w-3 h-3 rotate-45 ${on("second")}`} /><span className={`absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 rotate-45 ${on("first")}`} /><span className={`absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 rotate-45 ${on("third")}`} /></div>;
}
const Card = ({ icon: Icon, title, testid, children }) => <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={testid}><div className="label-overline flex items-center gap-2 mb-3"><Icon className="w-4 h-4 text-[#E63946]" />{title}</div>{children}</div>;
const Placeholder = ({ icon: Icon, label }) => <div className="border border-dashed border-slate-200 rounded-sm p-3 text-center text-slate-400"><Icon className="w-4 h-4 mx-auto" /><div className="text-[10px] uppercase tracking-wider mt-1">{label}</div></div>;
const Shell = ({ children }) => <div className="min-h-screen bg-[#F1F3F6]"><Header />{children}</div>;
const Empty = ({ children }) => <div className="py-16 text-center text-sm text-slate-400">{children}</div>;
